// Player archetypes + the Five-Imbalances profile + ActionModel. ENGINE_DESIGN.md §1, §4.3.
// An archetype is DATA (a JSON record); D1 authors 12 of these validated against ArchetypeSchema.
import { z } from 'zod';
import { SizingIntentSchema } from './action';
import { ConfidenceSchema } from './buckets';
export const ARCHETYPE_IDS = [
    'rock',
    'sticker',
    'firestarter',
    'taxCollector',
    'surgeon',
    'sheriff',
    'scaredStack',
    'waitingRoom',
    'tourist',
    'bully',
    'professor',
    'volcano',
    // D2 local live-pool additions (BUILD_PLAN D2; field guide §11). Field-guide types that already
    // map to D1 archetypes are aliases, not new ids: loose caller→sticker, tight reg→rock/waitingRoom,
    // aggressive reg→bully/firestarter, social rec→tourist, scared-money→scaredStack.
    'promoChaser',
    'tableCaptain',
    'shortStackGambler',
];
export const ArchetypeIdSchema = z.enum(ARCHETYPE_IDS);
/** Five-Imbalances profile (research/02 §5a) - the axes exploit rules key on. */
export const ImbalanceProfileSchema = z.object({
    bettingVolume: z.enum(['overBluffs', 'overCalls', 'overFolds', 'balanced']),
    elasticity: z.enum(['inelastic', 'elastic', 'normal']),
    polarity: z.enum(['tooPolar', 'tooMerged', 'normal']),
    boardCoverage: z.enum(['narrow', 'normal']),
    equityMgmt: z.enum(['overvaluesPairs', 'undervaluesDraws', 'normal']),
});
/** Coach-facing summary of the five axes (ARCHITECTURE.md §5 villain.imbalances). Same shape as
 *  the profile; named separately because the contract names it distinctly. */
export const ImbalanceSummarySchema = ImbalanceProfileSchema;
/** P(action | comboClass) - a sparse table keyed by a ComboClassKey string (ENGINE_DESIGN.md §4.3).
 *  Class-level defaults live under the same key space; probabilities are in [0,1]. */
export const SparseProbTableSchema = z.record(z.string(), z.number().min(0).max(1));
/** How an archetype acts AND how its range narrows - one table, two uses (ENGINE_DESIGN.md §4.3). */
export const ActionModelSchema = z.object({
    continueProb: SparseProbTableSchema,
    raiseProb: SparseProbTableSchema,
    /** scales air/weak-draw aggression. */
    bluffWeight: z.number(),
    sizingHabits: z.array(z.object({
        context: z.string(),
        intent: SizingIntentSchema,
        prob: z.number().min(0).max(1),
    })),
});
export const ArchetypeSchema = z.object({
    id: ArchetypeIdSchema,
    label: z.string(),
    description: z.string(),
    /** observable tells for the read trainer. */
    signals: z.array(z.string()),
    imbalances: ImbalanceProfileSchema,
    /** perceived-range update rate (ENGINE_DESIGN.md §4.5): 0 = never updates, 1 = updates sharply. */
    observance: z.number().min(0).max(1).optional(),
    preflop: z.object({
        rangeSetId: z.string(),
        vpipScale: z.number(),
        threeBetScale: z.number(),
        limpBias: z.number().min(0).max(1),
        openSizeBB: z.tuple([z.number(), z.number()]),
    }),
    postflop: ActionModelSchema,
    sizingTells: z
        .array(z.object({
        pattern: z.enum(['overbet', 'donkLarge', 'minRaise', 'suddenRaise']),
        meaning: z.enum(['valueHeavy', 'bluffHeavy', 'polarized', 'unclear']),
        confidence: ConfidenceSchema,
    }))
        .optional(),
});
//# sourceMappingURL=archetype.js.map